/**
 * EntityFormField compound sub-component.
 *
 * Renders the appropriate base input component for a single field based on
 * the field's configuration. Handles field lookup, override merging,
 * visibility checks, component mapping, and textarea swapping.
 *
 * The component wraps its output in a `<div>` with optional `col-span-{n}`
 * classes for grid layout integration. CSS overrides are merged from
 * config-level, field-level, and prop-level sources.
 *
 * @see Spec section 3.5 for the field-to-component mapping table.
 * @see types.ts `EntityFormFieldProps` and `FieldConfig` for shape details.
 */

import * as React from 'react';
import { Info } from 'lucide-react';
import { Icon, Tooltip } from '@components/base/display';
import { useEntityFormContext } from '../context/EntityFormContext';
import { mapFieldToComponent } from '../utils/fieldMapper';
import { DENSITY_TO_FIELD_SIZE, DEFAULT_LAYOUT } from '../constants';
import { TextArea } from './TextArea';
import type { EntityFormFieldProps, FieldConfig, TextareaOptions } from '../types';

// ---------------------------------------------------------------------------
// Column Span Mapping
// ---------------------------------------------------------------------------

/**
 * Maps numeric column spans to their Tailwind `col-span-{n}` classes.
 * Only applied when colSpan is explicitly set.
 */
const COL_SPAN_CLASSES: Record<number, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
  7: 'col-span-7',
  8: 'col-span-8',
  9: 'col-span-9',
  10: 'col-span-10',
  11: 'col-span-11',
  12: 'col-span-12',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Merges overrides into the base field config.
 * Override values win over config values.
 *
 * @param fieldConfig - The base field configuration from `config.fields`.
 * @param overrides   - Partial overrides from the component props.
 * @returns The merged field configuration.
 */
function mergeFieldConfig(
  fieldConfig: FieldConfig,
  overrides: Partial<FieldConfig> | undefined,
): FieldConfig {
  if (overrides) {
    return { ...fieldConfig, ...overrides };
  }
  return fieldConfig;
}

/**
 * Builds the wrapper class name by merging config-level, field-level,
 * and prop-level CSS overrides.
 *
 * @param cssOverride   - Global field CSS override from `config.cssOverrides?.field`.
 * @param fieldOverride - Per-field CSS override from `config.cssOverrides?.fieldOverrides?.[name]`.
 * @param className     - Additional class name from props.
 * @returns The merged class string, or `undefined` when all sources are empty.
 */
function buildWrapperClassName(
  cssOverride: string | undefined,
  fieldOverride: string | undefined,
  className: string | undefined,
): string | undefined {
  const merged = [cssOverride, fieldOverride, className]
    .filter(Boolean)
    .join(' ');
  return merged || undefined;
}

/**
 * Renders the per-field tooltip affordance: an info icon, anchored next to the
 * field label, that reveals `fieldConfig.tooltip` on hover/focus via the shared
 * {@link Tooltip} base component.
 *
 * Design — info-icon-on-label, **not** helperText pass-through (decision (a),
 * feasibility §4.1): the base inputs render their own label internally, so we
 * overlay a compact icon at the top-right of the field wrapper, aligned with the
 * label row. This matches the PM verb "add a tooltip to a field" and keeps
 * `helperText` free for always-visible static help — a field may carry **both**
 * (tooltip on the icon, helperText below the input) and they render independently.
 *
 * The icon lives in a real `<button type="button">` so it is keyboard-reachable
 * and focusable; the `Tooltip` wires `aria-describedby` and shows on focus too.
 * The button carries an `aria-label` derived from the field label, and the inner
 * `Icon` is decorative (no redundant label).
 *
 * Returns `null` when there is no tooltip, so fields without one render exactly
 * as before (no icon, no wrapper change) — a strict superset of prior behaviour.
 */
function FieldTooltip({
  tooltip,
  label,
  name,
}: Readonly<{ tooltip: string | undefined; label: string; name: string }>):
  | React.ReactElement
  | null {
  if (!tooltip) {
    return null;
  }

  return (
    <span className="absolute right-0 top-0 z-10 flex items-center">
      <Tooltip content={tooltip}>
        <button
          type="button"
          // Non-submitting affordance; purely reveals contextual help on
          // hover/focus. Visual styling kept minimal and neutral.
          className="inline-flex items-center justify-center rounded-full p-0.5 text-secondary-500 transition-colors hover:text-secondary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200"
          aria-label={`${label} — more information`}
          data-testid={`entity-form-field-tooltip-${name}`}
        >
          <Icon icon={Info} size="sm" aria-hidden />
        </button>
      </Tooltip>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders the appropriate base input component for a single named field.
 *
 * Consumes form state from `useEntityFormContext()`, resolves the field
 * configuration, and delegates rendering to the mapped base component.
 * Handles the textarea swap from the fieldMapper placeholder to the
 * dedicated TextArea adapter.
 *
 * Must be rendered inside an `<EntityForm>` provider.
 *
 * @example
 * ```tsx
 * <EntityForm config={config} form={form}>
 *   <EntityForm.Field name="firstName" />
 *   <EntityForm.Field name="email" colSpan={6} />
 *   <EntityForm.Field name="bio" overrides={{ placeholder: 'Tell us about yourself' }} />
 * </EntityForm>
 * ```
 *
 * @see EntityFormActions for the action button bar companion component.
 */
export function EntityFormField({
  name,
  overrides,
  className,
  colSpan: colSpanProp,
}: Readonly<EntityFormFieldProps>): React.ReactElement | null {
  const { config, form } = useEntityFormContext();

  // --- Resolve field config with overrides ---

  const baseFieldConfig = config.fields[name];
  if (!baseFieldConfig) {
    return null;
  }

  const merged = mergeFieldConfig(baseFieldConfig, overrides);

  const density = config.layout?.density ?? DEFAULT_LAYOUT.density;
  const fieldConfig =
    merged.size === undefined
      ? { ...merged, size: DENSITY_TO_FIELD_SIZE[density] }
      : merged;

  // --- Visibility check ---

  if (!form.isFieldVisible(name)) {
    return null;
  }

  // --- Get field props from form state ---

  const fieldProps = form.getFieldProps(name);

  // --- Map to component ---

  const mapping = mapFieldToComponent(fieldConfig, fieldProps);

  // --- Resolve rendered component ---

  let rendered: React.ReactElement;

  if (fieldConfig.type === 'textarea') {
    // Swap the fieldMapper placeholder (TextField with as='textarea') to TextArea
    const opts = fieldConfig.options as TextareaOptions | undefined;
    const { as: _as, ...restProps } = mapping.props;

    rendered = (
      <TextArea
        {...restProps}
        rows={opts?.rows}
        maxLength={opts?.maxLength}
        resize={opts?.resize}
      />
    );
  } else {
    const Component = mapping.component;
    rendered = <Component {...mapping.props} />;
  }

  // --- Build wrapper classes ---

  const colSpan = colSpanProp ?? fieldConfig.colSpan;
  const colSpanClass = colSpan === undefined ? undefined : COL_SPAN_CLASSES[colSpan];

  const wrapperClassName = buildWrapperClassName(
    config.cssOverrides?.field,
    config.cssOverrides?.fieldOverrides?.[name],
    className,
  );

  // Tooltip is rendered as an icon anchored to the label row, so the wrapper
  // becomes a positioning context — but ONLY when a tooltip is present, so
  // fields without one keep byte-identical wrapper classes (and none at all
  // when no colSpan / overrides exist).
  const hasTooltip = Boolean(fieldConfig.tooltip);
  const positioningClass = hasTooltip ? 'relative' : undefined;

  const containerClasses = [positioningClass, colSpanClass, wrapperClassName]
    .filter(Boolean)
    .join(' ') || undefined;

  // --- Render ---

  return (
    <div className={containerClasses} data-testid={`entity-form-field-${name}`}>
      <FieldTooltip tooltip={fieldConfig.tooltip} label={fieldConfig.label} name={name} />
      {rendered}
    </div>
  );
}

EntityFormField.displayName = 'EntityFormField';
